import React, { useEffect, useMemo, useState } from "react";
import { whatsNewApi, orgKey } from "./whatsNewApi";

/**
 * Choose which organizations receive one update.
 *
 * Selection is keyed on `server:org_id`, never `org_id` alone — the same
 * number is a different customer on a different eMedHub deployment, so a bare
 * id would send one hospital's update to an unrelated one elsewhere.
 *
 * `value` / `onChange` carry [{ server_code, org_id, org_name }].
 */
export default function OrgPicker({ value = [], onChange, disabled = false }) {
    const [orgs, setOrgs] = useState([]);
    const [servers, setServers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [server, setServer] = useState("");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const res = await whatsNewApi.getOrgs({ reachable: 1 });
            setOrgs(res.data || []);
            setServers(res.servers || []);
        } catch (e) {
            setErr(e.message || "Could not load organizations.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const selected = useMemo(
        () => new Set(value.map((v) => orgKey(v.server_code, v.org_id))),
        [value]
    );

    const shown = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return orgs.filter((o) => {
            if (server && o.server_code !== server) return false;
            if (!needle) return true;
            return (
                (o.org_name || "").toLowerCase().includes(needle) ||
                String(o.org_id).includes(needle)
            );
        });
    }, [orgs, q, server]);

    const toggle = (o) => {
        if (disabled) return;
        const key = orgKey(o.server_code, o.org_id);
        if (selected.has(key)) {
            onChange(
                value.filter((v) => orgKey(v.server_code, v.org_id) !== key)
            );
        } else {
            onChange([
                ...value,
                { server_code: o.server_code, org_id: o.org_id, org_name: o.org_name },
            ]);
        }
    };

    const sync = async () => {
        setSyncing(true);
        try {
            await whatsNewApi.syncOrgs();
            await load();
        } catch (e) {
            setErr(e.message || "Sync failed.");
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div>
            <div className="wn-row" style={{ marginBottom: 8 }}>
                <input
                    className="wn-input"
                    placeholder="Search organizations…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    disabled={disabled}
                />
                {servers.length > 1 && (
                    <select
                        className="wn-select"
                        value={server}
                        onChange={(e) => setServer(e.target.value)}
                        disabled={disabled}
                    >
                        <option value="">All deployments</option>
                        {servers.map((s) => (
                            <option key={s.code} value={s.code}>{s.label || s.code}</option>
                        ))}
                    </select>
                )}
                <button
                    className="wn-btn wn-btn--sm"
                    style={{ flex: "0 0 auto", minWidth: 0 }}
                    onClick={sync}
                    disabled={syncing || disabled}
                    title="Re-read organization_details on every deployment"
                >
                    {syncing ? "Syncing…" : "Sync"}
                </button>
            </div>

            {/* A failed request and an empty directory look identical on screen
                but need different fixes, so they are never merged. */}
            {err && (
                <div className="wn-hint wn-hint--warn">
                    Could not load organizations: {err}
                </div>
            )}

            {!loading && !err && orgs.length === 0 && (
                <div className="wn-hint wn-hint--warn">
                    No organizations in the directory yet. Run
                    <code> manage.py rn_orgs </code> on FixDesk, or press Sync above.
                </div>
            )}

            <div className="wn-orgpick">
                {loading && <div style={{ padding: 12, fontSize: 13 }}>Loading…</div>}
                {!loading && shown.length === 0 && orgs.length > 0 && (
                    <div style={{ padding: 12, fontSize: 13 }}>No match.</div>
                )}
                {shown.map((o) => {
                    const key = orgKey(o.server_code, o.org_id);
                    return (
                        <label key={key}>
                            <input
                                type="checkbox"
                                checked={selected.has(key)}
                                onChange={() => toggle(o)}
                                disabled={disabled}
                            />
                            <span>{o.org_name || `Org ${o.org_id}`}</span>
                            {o.client_name && (
                                <span className="wn-org" title="Mapped FixDesk client">
                                    {o.client_name}
                                </span>
                            )}
                            {servers.length > 1 && (
                                <span className="wn-srv">{o.server_label || o.server_code}</span>
                            )}
                        </label>
                    );
                })}
            </div>

            <div className="wn-hint">
                {value.length} organization{value.length === 1 ? "" : "s"} selected.
            </div>
        </div>
    );
}
